import { fmtDate, siteTodayKey, visitActualDateLabel, visitWhen } from '@/lib/dates';
import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Activity, AlertTriangle, Calendar, CheckCircle2, Download, ExternalLink, Info, LayoutList, Loader2, Plus, X } from 'lucide-react';
import { subjects, visits as visitsApi, type VisitRow } from '@/api/client';
import { PageHeader } from '@/components/layout/PageHeader';
import { BlindedNotice } from '@/components/layout/BlindedNotice';
import { encryptId, resolveId } from '@/lib/encryptId';
import { RemoteConsentTrigger } from '@/pages/subjects/RemoteConsentTrigger';
import { WithdrawConsentTrigger } from '@/pages/subjects/WithdrawConsentTrigger';
import { SubjectStatusActions } from '@/pages/subjects/SubjectStatusActions';
import { EmergencyUnblindAction } from '@/pages/subjects/EmergencyUnblindAction';
import { AdverseEventsSection } from '@/pages/subjects/AdverseEventsSection';
import { MedicalHistorySection } from '@/pages/subjects/MedicalHistorySection';
import { ConmedsSection } from '@/pages/subjects/ConmedsSection';
import { SubjectProfileSection } from '@/pages/subjects/SubjectProfileSection';
import { SubjectSignedConsentsSection } from '@/pages/subjects/SubjectSignedConsentsSection';
import { SubjectOnboardingDetailsSection } from '@/pages/subjects/SubjectOnboardingDetailsSection';
import { humanStatus } from '@/lib/humanise';
import { SUBJECT_ID_LABEL, SUBJECT_REFERENCE_ID_LABEL, subjectDisplayId, subjectIvrsId } from '@/lib/subjectLabel';
import { humanSubjectStatus, subjectStatusBadgeClass } from '@/lib/subjectStatus';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';


export function SubjectDetailPage() {
  const { user } = useAuth();
  const isMonitor = user?.role === 'monitor';
  const { id } = useParams<{ id: string }>();
  // Decrypt the URL param client-side so we always send a raw UUID to the
  // backend — avoids mismatches when frontend/backend encryption keys differ.
  const subjectId = resolveId(id ?? '');
  const q = useQuery({
    queryKey: ['subject', subjectId],
    queryFn: () => subjects.detail(subjectId),
    enabled: !!subjectId,
  });

  const exportJson = useMutation({
    mutationFn: () => subjects.exportJson(subjectId),
    onSuccess: (data) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const ref = (data as { subject?: { reference_number?: string } })?.subject?.reference_number ?? subjectId;
      const a = document.createElement('a');
      a.href = url;
      a.download = `subject-${ref}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Subject exported as JSON.');
    },
    onError: (e) => toast.error(errorText(e, 'Export failed.')),
  });

  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2 p-8 text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className={`flex items-start gap-2 p-4 text-sm ${errorToneClass(q.error)}`}>
        {isPermissionError(q.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
        <span>{errorText(q.error, 'Failed to load subject.')}</span>
      </div>
    );
  }
  if (!q.data) return null;

  if (q.data.blinded) {
    return (
      <div className="space-y-6">
        <PageHeader backTo="/subjects" title="Subject" />
        <BlindedNotice contentLabel="Subject details" roleMasterRef={q.data.blinded_role_master_ref ?? null} />
      </div>
    );
  }

  const { subject, phi, visits, wizard_state } = q.data;
  // IVRS ID in the heading; GET /subjects/{id} carries it under wizard_state.
  const ivrsId = subjectIvrsId({
    external_ref: subject.external_ref,
    ivrf_number: subject.ivrf_number ?? wizard_state?.ivrf_number ?? null,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/subjects"
        title={`Subject ${subjectDisplayId({ external_ref: subject.external_ref, ivrf_number: ivrsId })}`}
        subtitle={
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px]">
            <span className="text-ink-muted">
              {ivrsId
                ? `${SUBJECT_REFERENCE_ID_LABEL}: ${subject.external_ref}`
                : `${SUBJECT_ID_LABEL}: not issued yet`}
            </span>
            <span className={`badge ${subjectStatusBadgeClass(subject.status)}`}>
              {humanSubjectStatus(subject.status)}
            </span>
            {subject.re_consent_required && (
              <span className="badge badge-warning">Re-consent required</span>
            )}
          </div>
        }
        actions={
          <>
            <Link
              to={`/subjects/${encryptId(subject.subject_id)}/visits-esource`}
              className="btn-secondary inline-flex items-center gap-1 text-xs"
            >
              <LayoutList className="h-3.5 w-3.5" /> Visits &amp; eSource
            </Link>
            {!isMonitor && (
              <button
                type="button"
                onClick={() => exportJson.mutate()}
                disabled={exportJson.isPending}
                className="btn-secondary inline-flex items-center gap-1 text-xs disabled:opacity-40"
                title="Export this subject's data as JSON"
              >
                {exportJson.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export JSON
              </button>
            )}
            <EmergencyUnblindAction subjectId={subject.subject_id} studyBlindingType={q.data.study_blinding_type} />
            {/* Monitor is read-only — hides both "Mark as Completed" and
                "Withdraw subject" status transitions in this menu. */}
            {!isMonitor && <SubjectStatusActions subject={subject} />}
          </>
        }
      />

      {/* DREAM-26 — Re-Consent banner. Blocks further study activity until completed. */}
      {subject.re_consent_required && (
        <div className="rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
          <div className="font-semibold text-amber-900">Re-Consent Required</div>
          <p className="mt-1 text-amber-800">
            A new consent document version is active for this study. New visits, data entry, and
            other study activity are blocked for this subject until re-consent is completed.
          </p>
          <Link
            to={`/subjects/onboarding?subject_id=${subject.subject_id}&reconsent=1`}
            className="mt-2 inline-block rounded bg-amber-700 px-3 py-1 text-xs font-medium text-white hover:bg-amber-800"
          >
            Start Re-Consent
          </Link>
        </div>
      )}

      <SubjectProfileSection subjectId={subject.subject_id} phi={phi} wizardState={wizard_state} />

      <SubjectOnboardingDetailsSection
        subjectId={subject.subject_id}
        wizardState={wizard_state}
        consentSummary={q.data.consent_summary ?? null}
      />

      <SubjectSignedConsentsSection subjectId={subject.subject_id} />

      <RemoteConsentTrigger subjectId={subject.subject_id} />
      {!isMonitor && (
        <WithdrawConsentTrigger
          subjectId={subject.subject_id}
          status={subject.status}
          activeConsentRecordId={(subject as unknown as { active_consent_record_id?: string | null }).active_consent_record_id ?? null}
        />
      )}

      {/* Phase 5 — Adverse Events section (AC1 entry surface). */}
      <AdverseEventsSection subjectId={subject.subject_id} studyId={subject.study_id} />

      {/* 2026-10-08 — subject clinical data from the CTMS schema spec.
          Placed after Adverse Events because conmeds and medical history are
          the context a reviewer wants while reading an AE. */}
      <MedicalHistorySection subjectId={subject.subject_id} />
      <ConmedsSection subjectId={subject.subject_id} />

      <SubjectVisitSchedules
        subjectId={subject.subject_id}
        studyId={subject.study_id}
        legacyVisits={visits}
        reConsentRequired={!!subject.re_consent_required}
        subjectStatus={subject.status}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// DREAM-21 — Visit Schedules embedded on Subject page (Jawahar comment 28/May/26)
// ---------------------------------------------------------------------------

function SubjectVisitSchedules({
  subjectId, studyId, legacyVisits, reConsentRequired, subjectStatus,
}: {
  subjectId: string;
  studyId: string;
  legacyVisits: Array<{ visit_id: string; visit_label?: string; scheduled_at?: string | null; completed_at?: string | null; unscheduled?: boolean }>;
  reConsentRequired?: boolean;
  subjectStatus?: string;
}) {
  const { user } = useAuth();
  const isMonitor = user?.role === 'monitor';
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'timeline'>('table');
  const [draft, setDraft] = useState({
    visit_date: siteTodayKey(),
    reason: '',
  });

  // Read from the Sprint-2 endpoint so we get the rich columns
  // (visit_name, planned_date, status, visit_type, window_compliance).
  const sprint2Q = useQuery({
    queryKey: ['subject-visits-sprint2', subjectId],
    queryFn: () => visitsApi.list({ scope: 'subject', subject_id: subjectId }),
  });

  const createUnscheduled = useMutation({
    mutationFn: () => visitsApi.createUnscheduled({
      study_id: studyId,
      subject_id: subjectId,
      visit_date: new Date(draft.visit_date).toISOString(),
      reason: draft.reason || undefined,
    }),
    onSuccess: () => {
      toast.success('Unscheduled visit added');
      setShowAddModal(false);
      setDraft({ visit_date: siteTodayKey(), reason: '' });
      qc.invalidateQueries({ queryKey: ['subject-visits-sprint2', subjectId] });
      qc.invalidateQueries({ queryKey: ['subject', subjectId] });
    },
    onError: (err) => toast.error(errorText(err, 'Failed to add visit')),
  });

  // Merge Sprint-2 + legacy rows (de-dup by visit_id). Sprint-2 wins if both present.
  const sprint2Visits = sprint2Q.data?.visits ?? [];
  const sprint2Ids = new Set(sprint2Visits.map((v) => v.visit_id));
  const merged: VisitRow[] = [
    ...sprint2Visits,
    ...legacyVisits
      .filter((v) => !sprint2Ids.has(v.visit_id))
      .map((v) => ({
        visit_id: v.visit_id,
        subject_id: subjectId,
        study_id: studyId,
        visit_label: v.visit_label,
        scheduled_at: v.scheduled_at,
        completed_at: v.completed_at,
        unscheduled: v.unscheduled,
      } as VisitRow)),
  ];
  // Sort by planned/scheduled date.
  merged.sort((a, b) => {
    const da = visitWhen(a) ?? '';
    const db = visitWhen(b) ?? '';
    return da.localeCompare(db);
  });

  return (
    <section className="card p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink-primary">
            Visit schedules ({merged.length})
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center gap-0.5 rounded-md border border-surface-border p-0.5">
            <button type="button"
              onClick={() => setViewMode('table')}
              className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium transition-colors ${viewMode === 'table' ? 'bg-accent-50 text-accent-800' : 'text-ink-secondary hover:bg-surface-muted'}`}>
              <LayoutList className="h-3 w-3" /> Table
            </button>
            <button type="button"
              onClick={() => setViewMode('timeline')}
              className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium transition-colors ${viewMode === 'timeline' ? 'bg-accent-50 text-accent-800' : 'text-ink-secondary hover:bg-surface-muted'}`}>
              <Activity className="h-3 w-3" /> Timeline
            </button>
          </div>
          <button type="button" className="btn-ghost text-[10px]"
            onClick={() => navigate(`/visits?subject_id=${subjectId}`)}>
            <ExternalLink className="h-3 w-3" /> Open in calendar
          </button>
          {!isMonitor && (() => {
            const isTerminal = subjectStatus === 'withdrawn' || subjectStatus === 'completed';
            const blockedReason = isTerminal
              ? `Cannot add visits — subject is ${subjectStatus}`
              : reConsentRequired
                ? 'Re-consent is required before adding new visits'
                : undefined;
            return (
              <button
                type="button"
                className="btn-secondary text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!!blockedReason}
                title={blockedReason}
                onClick={() => setShowAddModal(true)}
              >
                <Plus className="h-3.5 w-3.5" /> Add Unscheduled Visit
              </button>
            );
          })()}
        </div>
      </div>
      {sprint2Q.isLoading ? (
        <div className="flex items-center justify-center gap-2 py-6 text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : merged.length === 0 ? (
        <p className="text-sm text-ink-muted">No visits scheduled yet.</p>
      ) : viewMode === 'timeline' ? (
        <VisitTimeline visits={merged} />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="px-3 py-2">Visit</th>
                <th className="px-3 py-2">Planned</th>
                <th className="px-3 py-2">Actual date</th>
                <th className="px-3 py-2">Window</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Open</th>
              </tr>
            </thead>
            <tbody>
              {merged.map((v) => {
                const isUnscheduled = v.visit_type === 'unscheduled' || v.unscheduled === true;
                const planned = visitWhen(v);
                const actual = visitActualDateLabel(v);
                return (
                  <tr key={v.visit_id} className="border-b border-surface-border last:border-0">
                    <td className="px-3 py-2 text-xs font-medium">
                      {v.visit_name ?? v.visit_label ?? 'Visit'}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {planned ? fmtDate(planned) : '—'}
                    </td>
                    <td className={`px-3 py-2 text-xs ${actual.recorded ? '' : 'text-ink-muted'}`}>
                      {actual.text}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-muted">
                      {v.nominal_day != null
                        ? `Day ${v.nominal_day} ${v.window_minus_days != null || v.window_plus_days != null
                          ? `± ${Math.max(v.window_minus_days ?? 0, v.window_plus_days ?? 0)}`
                          : ''}`
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {isUnscheduled
                        ? <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">Unscheduled</span>
                        : <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-sky-50 text-sky-700 border border-sky-200">Scheduled</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {v.status ? humanStatus(v.status) : v.completed_at ? 'Completed' : 'Planned'}
                    </td>
                    <td className="px-3 py-2 text-right text-xs">
                      <Link to={`/visits/${encryptId(v.visit_id)}`} className="text-accent-700 hover:underline">
                        Open
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
          <form className="card w-full max-w-md space-y-4 p-6"
            onSubmit={(e) => { e.preventDefault(); createUnscheduled.mutate(); }}>
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-semibold">Add Unscheduled Visit</h3>
              <button type="button" onClick={() => setShowAddModal(false)}><X className="h-4 w-4" /></button>
            </div>
            <div>
              <label className="label text-xs">Visit date *</label>
              <input className="input" type="date" required value={draft.visit_date}
                onChange={(e) => setDraft({ ...draft, visit_date: e.target.value })} />
              <p className="mt-1 text-[10px] text-ink-muted">Defaults to today; change if the visit happened on another date.</p>
            </div>
            <div>
              <label className="label text-xs">Reason / note (optional)</label>
              <textarea className="input min-h-[60px]" value={draft.reason}
                onChange={(e) => setDraft({ ...draft, reason: e.target.value })} maxLength={500} />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={createUnscheduled.isPending}>
                {createUnscheduled.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Add visit
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// AC8 — Chronological visit timeline
// ---------------------------------------------------------------------------

const STATUS_DOT_CLS: Record<string, string> = {
  completed: 'bg-success-500 border-success-600',
  missed: 'bg-warning-400 border-warning-500',
  cancelled: 'bg-danger-400 border-danger-500',
  planned: 'bg-white border-ink-300',
};

function VisitTimeline({ visits: vs }: { visits: VisitRow[] }) {
  const ordered = [...vs].sort((a, b) => {
    const da = visitWhen(a) ?? '';
    const db = visitWhen(b) ?? '';
    return da.localeCompare(db);
  });

  return (
    <div className="overflow-x-auto py-2">
      <div className="relative flex min-w-max items-center px-6">
        {/* Connecting rail */}
        <div className="pointer-events-none absolute left-10 right-10 top-[54px] h-0.5 bg-surface-border" />

        {ordered.map((v) => {
          const isU = v.visit_type === 'unscheduled' || v.unscheduled === true;
          const status = v.status ?? (v.completed_at ? 'completed' : 'planned');
          // Unscheduled planned visits get an amber tint so they read differently
          // from protocol-scheduled planned visits — without using border-dashed
          // (which renders poorly on rounded circles).
          const dotCls = isU && status === 'planned'
            ? 'bg-amber-50 border-amber-300'
            : STATUS_DOT_CLS[status] ?? STATUS_DOT_CLS.planned;
          const planned = visitWhen(v);
          const actual = visitActualDateLabel(v);

          const visitDisplayName = v.visit_name ?? v.visit_label ?? (isU ? 'Unscheduled' : 'Visit');

          return (
            <div key={v.visit_id} className="relative z-10 flex w-20 flex-col items-center">
              {/* Fixed-height header so every dot sits on the same rail Y */}
              <div className="mb-2 flex h-8 w-full flex-col items-center justify-end text-center">
                <p className="truncate text-[10px] font-medium text-ink-primary" title={visitDisplayName}>
                  {visitDisplayName}
                </p>
                <p className="text-[9px] text-ink-muted">
                  {planned ? fmtDate(planned) : '—'}
                </p>
              </div>

              {/* Dot — same solid circle shape for all visit types */}
              <Link
                to={`/visits/${encryptId(v.visit_id)}`}
                className={`flex h-8 w-8 items-center justify-center rounded-full border-2 shadow-sm transition-all hover:ring-2 hover:ring-accent-300 ${dotCls}`}
                title={`${visitDisplayName} · ${humanStatus(status)}`}
              >
                {status === 'completed' && <CheckCircle2 className="h-4 w-4 text-white" />}
                {status === 'missed' && <span className="text-[8px] font-bold text-warning-800">M</span>}
                {status === 'cancelled' && <X className="h-3 w-3 text-white" />}
                {isU && status === 'planned' && <span className="text-[8px] font-bold text-amber-600">U</span>}
              </Link>

              {/* Protocol day / type label below dot */}
              <p className="mt-2 text-[9px] text-ink-muted">
                {v.nominal_day != null ? `Day ${v.nominal_day}` : isU ? 'Unsched.' : ''}
              </p>
              {/* Actual date (23 Oct 2026) — the date above is the appointment. */}
              <p
                className={`text-center text-[9px] ${actual.recorded ? 'font-medium text-ink-secondary' : 'text-ink-muted'}`}
                title="Actual date"
              >
                {actual.recorded ? `Actual ${actual.text}` : actual.text}
              </p>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center gap-4 px-6 text-[10px] text-ink-muted">
        {[
          { cls: 'bg-white border-ink-300', label: 'Planned' },
          { cls: 'bg-success-500 border-success-600', label: 'Completed' },
          { cls: 'bg-warning-400 border-warning-500', label: 'Missed' },
          { cls: 'bg-danger-400 border-danger-500', label: 'Cancelled' },
        ].map(({ cls, label }) => (
          <span key={label} className="flex items-center gap-1.5">
            <span className={`h-3 w-3 rounded-full border-2 inline-block ${cls}`} />
            {label}
          </span>
        ))}
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-full border-2 border-amber-300 bg-amber-50 inline-block" />
          Unscheduled
        </span>
      </div>
    </div>
  );
}
